import { ConfirmModal } from '@src/components/common/ConfirmModal';
import { KebabButton } from '@src/components/common/contextMenuStyles';
import { FilterRowCount } from '@src/components/common/filterDropdownStyles';
import Modal from '@src/components/common/Modal';
import SavedSegmentRowActionsMenu from '@src/components/common/SavedSegmentRowActionsMenu';
import { COMPACT_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { type TopicFilterMode } from '@src/components/common/TopicFilterDropdown';
import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import CopyContentModal from '@src/components/stage/CopyContentModal';
import CheckIconAlt from '@src/svgs/CheckIconAlt';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import PencilIcon from '@src/svgs/PencilIcon';
import ReelIcon from '@src/svgs/ReelIcon';
import { DEFAULT_SAVED_SEGMENT_COLOR } from '@src/utility/savedSegmentColors';
import React, { useId, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

export interface SavedSegmentFilterItem {
  key: string;
  name: string;
  color: string;
  rangeCount: number;
}

interface SavedSegmentsFilterDropdownProps {
  segments: SavedSegmentFilterItem[];
  /**
   * Selected segment keys. Empty array means "Show All" — every segment is
   * highlighted (highlight mode) / nothing is hidden (show-only mode).
   */
  selectedKeys: string[];
  onApply: (keys: string[]) => void;
  /** Explicit off state — when true no highlights show, regardless of selection. */
  hidden: boolean;
  onHiddenChange: (hidden: boolean) => void;
  mode: TopicFilterMode;
  onModeChange: (mode: TopicFilterMode) => void;
  /**
   * True while the Story Topics filter is in Show Only (PLAT-11182). The two
   * show-only sets intersect, so a selection could silently resolve to an
   * empty transcript — this filter goes inert until the other one is off.
   * A boolean rather than the caller-supplied `disabledReason` string its
   * Story Topics counterpart takes: this dropdown renders from `Transcript.tsx`
   * only, whose size ratchet leaves no room for the copy at the call site,
   * so it owns the wording here.
   */
  blockedByTopicFilter?: boolean;
  /**
   * Per-row highlight management (PLAT-11182), surfaced from a "more options"
   * kebab on each row — this menu is where users pick highlights, so it is
   * where they reach for acting on one. All three are optional: without them
   * the rows stay filter-only, which is what the hosts that don't manage
   * highlights get.
   *
   * `onCombine` merges a multi-section highlight into one clip (the host owns
   * the flow: service call, analytics, toast with an "Open" action).
   */
  onCombine?: (key: string) => void | Promise<void>;
  onRenameSegment?: (key: string, name: string) => void;
  onRemoveSegment?: (key: string) => void;
}

const BLOCKED_BY_TOPIC_FILTER =
  'Turn off "Show Only" in Story Topics to filter by highlights';

/* Matches the Story Manager's rename dialog (ActionsMenu) so every rename in
   the app is the same dialog at the same size. */
const RENAME_MODAL_STYLES = {
  height: 'unset',
  width: '440px',
  display: 'flex',
  gap: '29px',
  'flex-direction': 'column',
  'justify-content': 'center',
  'box-shadow': 'var(--elevation-toast)',
  'box-sizing': 'content-box',
};

/**
 * Header filter listing the story's saved segments, mirroring the Story Topics
 * dropdown so the two sit consistently in the transcript header. A
 * Show All / Show Only mode toggle sits on top; below it a Select All /
 * Deselect All bulk action and one row per segment (each with its color
 * indicator) let the user filter to all segments or a specific subset.
 * Rendered only when at least one segment exists.
 */
const SavedSegmentsFilterDropdown: React.FC<
  SavedSegmentsFilterDropdownProps
> = ({
  segments,
  selectedKeys,
  onApply,
  hidden,
  onHiddenChange,
  mode,
  onModeChange,
  blockedByTopicFilter = false,
  onCombine,
  onRenameSegment,
  onRemoveSegment,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Anchor on the container (trigger + fused nav) so the panel's right edge
  // lines up with the visible pill; clicks on the fused nav count as inside
  // the anchor and don't dismiss the menu.
  const anchorRef = useRef<HTMLDivElement>(null);
  const modeTooltipId = useId();
  const triggerTooltipId = useId();
  const combineTooltipId = useId();
  // Never inert the filter that currently owns Show Only: each dropdown blocks
  // the other, so blocking both would leave no way to turn either off.
  const isDisabled = blockedByTopicFilter && mode !== 'show-only';

  // Close on the way into the inert state — a menu opened before the other
  // filter switched to Show Only would otherwise stay up and stay clickable.
  if (isDisabled && isOpen) setIsOpen(false);

  // Portal + anchor the panel below the trigger (right-aligned), matching
  // TopicFilterDropdown — the hook owns dismissal (incl. focus moving into
  // the renderer iframe) and viewport clamping (PLAT-11039: replaced the
  // hand-rolled menuPos/useOutsideAlerter positioning).
  const { menuRef, style } = useAnchoredDropdown(
    isOpen,
    anchorRef,
    () => setIsOpen(false),
    480,
    { align: 'end', width: 280 },
  );

  // "Show All" is checked only when highlights are on with no subset selected.
  const showAll = !hidden && selectedKeys.length === 0;

  // Per-row management (PLAT-11182). Both actions close the dropdown and open
  // a dialog: renaming in place inside the menu was fragile (the field lost
  // focus to the surrounding portals and reverted), and a name is worth a
  // deliberate step. Deleting is destructive with no undo, so it confirms.
  const hasRowActions = !!onRenameSegment && !!onRemoveSegment;

  // Combining is promoted from the row's kebab to an inline button on the one
  // row the user has narrowed to — the state the old toolbar button used to
  // appear in (PLAT-11182). Show All / nothing selected / several selected all
  // leave it in the menu, where it still works per row.
  const promotedCombineKey =
    onCombine && !hidden && selectedKeys.length === 1
      ? (segments.find(s => s.key === selectedKeys[0] && s.rangeCount >= 2)
          ?.key ?? null)
      : null;
  // Combining hands the wait to the host's toast and closes the menu, like
  // the row's Rename and Delete do. It used to collapse this row to a busy
  // spinner, but a dropdown is dismissible mid-wait — one click on the
  // transcript and the only sign of progress was gone (docs/design-system.md
  // → Loading & Progress: an indicator lives in a control only when its
  // surface survives the wait).
  const handleCombine = (key: string) => {
    setIsOpen(false);
    void onCombine?.(key);
  };

  const [pendingRename, setPendingRename] =
    useState<SavedSegmentFilterItem | null>(null);
  const [pendingDelete, setPendingDelete] =
    useState<SavedSegmentFilterItem | null>(null);

  // Hand the row off to a dialog: close the menu first so the dialog isn't
  // competing with it, and so dismissing the dialog doesn't leave a stale
  // dropdown hanging over the transcript.
  const openRowDialog = (
    open: (segment: SavedSegmentFilterItem) => void,
    segment: SavedSegmentFilterItem,
  ) => {
    setIsOpen(false);
    open(segment);
  };

  const commitRename = (name: string) => {
    const trimmed = name.trim();
    const key = pendingRename?.key;
    setPendingRename(null);
    // An empty name would leave the row unlabeled and there is no placeholder
    // to fall back on here — treat it as a cancel.
    if (key && trimmed) onRenameSegment?.(key, trimmed);
  };

  const handleToggleShowAll = () => {
    if (showAll) {
      // Already showing every highlight → toggle them off.
      onHiddenChange(true);
    } else {
      // Off, or narrowed to a subset → show all again.
      onHiddenChange(false);
      onApply([]);
    }
  };

  const handleToggleSegment = (key: string) => {
    // Under "Show All" every row renders checked, so clicking one means
    // "hide just this one" — materialize the implicit all-selection first
    // (PLAT-11039). Picking any segment while hidden implies highlights on.
    const base = hidden
      ? []
      : showAll
        ? segments.map(s => s.key)
        : selectedKeys;
    const next = base.includes(key)
      ? base.filter(k => k !== key)
      : [...base, key];
    if (hidden) onHiddenChange(false);
    if (next.length === 0) {
      // Unchecking the last box means "none", not "all" — despite the empty
      // selection meaning Show All in the data model.
      onHiddenChange(true);
      onApply([]);
      return;
    }
    // Re-checking the full set collapses back to Show All (empty selection),
    // so segments created later stay auto-included.
    onApply(next.length === segments.length ? [] : next);
  };

  return (
    <Container ref={anchorRef}>
      {/* `aria-disabled`, not the native `disabled` attribute — a natively
          disabled button fires no pointer events, so the tooltip explaining
          WHY it is inert would never show (same reason as the toggle pills). */}
      <TriggerButton
        $disabled={isDisabled}
        aria-disabled={isDisabled}
        onClick={isDisabled ? undefined : () => setIsOpen(prev => !prev)}
        data-tooltip-id={triggerTooltipId}
        data-tooltip-content={isDisabled ? BLOCKED_BY_TOPIC_FILTER : undefined}
      >
        Highlights
        {/* Plain muted "(n)" rather than a filled badge (PLAT-11039) — the
            header row reads quieter, and it matches Story Topics. */}
        {selectedKeys.length > 0 && (
          <SelectionCount>({selectedKeys.length})</SelectionCount>
        )}
        <Caret $open={isOpen}>
          <ChevronDownIcon
            width="12"
            height="12"
            strokeColor={isDisabled ? 'var(--text-muted)' : 'var(--accent)'}
          />
        </Caret>
      </TriggerButton>
      {isDisabled && (
        <Tooltip
          id={triggerTooltipId}
          style={COMPACT_TOOLTIP_STYLE}
          place="top"
          positionStrategy="fixed"
        />
      )}

      {isOpen &&
        style &&
        ReactDOM.createPortal(
          <Dropdown ref={menuRef} style={style}>
            <ModeToggle role="group" aria-label="Highlights filter mode">
              <ModeButton
                type="button"
                $active={mode === 'highlight'}
                onClick={() => onModeChange('highlight')}
                aria-pressed={mode === 'highlight'}
                data-tooltip-id={modeTooltipId}
                data-tooltip-content="Mark the selected highlights in place — full transcript stays visible"
              >
                {/* "Show All" for the same reason as Story Topics: "Highlight"
                    as a mode name inside the Highlights menu was circular, and
                    the pair now names how much transcript stays visible. */}
                Show All
              </ModeButton>
              <ModeButton
                type="button"
                $active={mode === 'show-only'}
                onClick={() => onModeChange('show-only')}
                aria-pressed={mode === 'show-only'}
                data-tooltip-id={modeTooltipId}
                data-tooltip-content="Hide everything except the selected highlights"
              >
                Show Only
              </ModeButton>
            </ModeToggle>
            <Tooltip
              id={modeTooltipId}
              style={COMPACT_TOOLTIP_STYLE}
              place="top"
              positionStrategy="fixed"
            />

            {/* PLAT-11182: a bulk action, not a checkbox row — as a checkbox
                it sat in the list and read as one more highlight group. */}
            <ListActions>
              <SelectAllButton type="button" onClick={handleToggleShowAll}>
                {showAll ? 'Deselect All' : 'Select All'}
              </SelectAllButton>
            </ListActions>
            <SegmentList>
              {segments.map(segment => {
                // An empty selection implicitly selects every segment, so the
                // rows must read as checked too (PLAT-11039).
                const isSelected =
                  !hidden && (showAll || selectedKeys.includes(segment.key));
                return (
                  <SegmentItem
                    key={segment.key}
                    onClick={() => handleToggleSegment(segment.key)}
                  >
                    <CheckboxIndicator selected={isSelected}>
                      {isSelected && <CheckIconAlt />}
                    </CheckboxIndicator>
                    <ColorSwatch
                      $color={segment.color || DEFAULT_SAVED_SEGMENT_COLOR}
                    />
                    <SegmentLabel>{segment.name}</SegmentLabel>
                    {segment.rangeCount > 1 && (
                      <FilterRowCount>{segment.rangeCount}</FilterRowCount>
                    )}
                    {promotedCombineKey === segment.key && (
                      <CombineRowButton
                        type="button"
                        onClick={event => {
                          event.stopPropagation();
                          handleCombine(segment.key);
                        }}
                        aria-label={`Combine "${segment.name}" into a clip`}
                        data-tooltip-id={combineTooltipId}
                        data-tooltip-content="Combine into Clip"
                        data-testid="saved-segment-row-combine-inline"
                      >
                        <ReelIcon width="14" height="14" />
                      </CombineRowButton>
                    )}
                    {hasRowActions && (
                      <SavedSegmentRowActionsMenu
                        segmentName={segment.name}
                        onRename={() =>
                          openRowDialog(setPendingRename, segment)
                        }
                        onDelete={() =>
                          openRowDialog(setPendingDelete, segment)
                        }
                      />
                    )}
                  </SegmentItem>
                );
              })}
            </SegmentList>
            {promotedCombineKey && (
              <Tooltip
                id={combineTooltipId}
                style={COMPACT_TOOLTIP_STYLE}
                place="top"
                positionStrategy="fixed"
              />
            )}
          </Dropdown>,
          document.body,
        )}

      {/* Both dialogs render outside the dropdown's portal: the menu is closed
          by the time either opens, and a dialog nested in a menu that can be
          dismissed out from under it is how the in-place rename broke. */}
      {pendingRename && (
        <Modal isOpen closeModal={() => setPendingRename(null)}>
          <CopyContentModal
            name={pendingRename.name}
            tag="Highlight"
            type="rename"
            onCancel={() => setPendingRename(null)}
            onSave={commitRename}
            modalOverrides={{
              hideInputLabel: true,
              saveButtonContent: (
                <>
                  <PencilIcon
                    width="16"
                    height="16"
                    strokeColor="var(--background)"
                  />
                  Save Name
                </>
              ),
              wrapperStyles: RENAME_MODAL_STYLES,
            }}
          />
        </Modal>
      )}

      {/* Deleting a highlight cannot be undone, hence the confirm step
          (docs/design-system.md). */}
      {pendingDelete && (
        <ConfirmModal
          closeModal={() => setPendingDelete(null)}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            const key = pendingDelete.key;
            setPendingDelete(null);
            onRemoveSegment?.(key);
          }}
          title={`Delete "${pendingDelete.name}"?`}
          message="This highlight will be removed from the transcript. This cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}
    </Container>
  );
};

export default SavedSegmentsFilterDropdown;

const Container = styled.div`
  position: relative;
  display: inline-flex;
  align-items: stretch;
  height: 100%;
`;

/* PLAT-11039: borderless language-selector trigger recipe (TranslationToggle's
   \`.Selected\` override) — gray like the Quotes label, hover matches the
   header's PanelButton idiom. Selection is signaled by the badge, not a
   border. */
const TriggerButton = styled.button<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  height: 100%;
  border: none;
  border-radius: var(--radius-button);
  background: transparent;
  color: ${({ $disabled }) =>
    $disabled ? 'var(--text-muted)' : 'var(--text-secondary)'};
  /* Same size as the language trigger and the Quotes/Clips labels
     (PLAT-11039). */
  font-size: var(--text-xs);
  font-weight: 500;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  white-space: nowrap;
  transition: all 150ms ease-out;

  /* Inert: muted, no hover response — the tooltip carries the reason
     (PLAT-11182), matching the Quotes/Clips pills' blocked state. */
  &:hover {
    background-color: ${({ $disabled }) =>
      $disabled ? 'transparent' : 'var(--surface-elevated)'};
    color: ${({ $disabled }) =>
      $disabled ? 'var(--text-muted)' : 'var(--foreground)'};
  }
`;

const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  transition: transform var(--transition-fast);
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;

/* Selected-group count on the trigger — muted text, matching the per-row
   counts and the Story Topics trigger. */
const SelectionCount = styled.span`
  font-size: 11px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`;

/* Positioning (position/top/left/width/max-height/overflow/z-index) is
   supplied at runtime by useAnchoredDropdown via an inline style. Visuals
   match TopicFilterDropdown's menu (design-system menu density). */
const Dropdown = styled.div`
  background-color: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: var(--spacing-sm);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  box-shadow: var(--elevation-toast);
`;

/* Bulk-action row above the list (PLAT-11182), right-aligned per the
   Select All / Deselect All convention. Borderless rather than
   SpeakerFilterComponent's bordered pill: inside a 280px menu that already
   carries the mode toggle, a bordered button competed with it. */
const ListActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SelectAllButton = styled.button`
  border: none;
  background: transparent;
  padding: 2px 6px;
  border-radius: var(--radius-button);
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 150ms ease-out;

  &:hover {
    background-color: var(--surface-elevated);
    color: var(--foreground);
  }
`;

const SegmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 240px;
  overflow-y: auto;
`;

const SegmentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 8px;
  cursor: pointer;
  border-radius: var(--radius-button);
  transition: background-color 0.1s ease;

  &:hover {
    background-color: var(--surface-elevated);
  }
`;

/* Inline combine action, sitting left of the row's kebab (PLAT-11182). Icon
   only — the row is already dense — with the label carried by its tooltip.
   Built on the shared KebabButton so the row's two icon buttons are the same
   control, differing only by glyph. Deliberately NOT tinted with
   `--content-saved-segments`: that purple identifies highlight content in the
   transcript, and coloring an action with a content-identity token reads as a
   third accent next to the gold checkboxes with no meaning behind it. */
const CombineRowButton = styled(KebabButton)`
  margin-left: 0;
  padding: 2px;
  flex-shrink: 0;
`;

const CheckboxIndicator = styled.div<{ selected: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid var(--border);
  background-color: ${props =>
    props.selected ? 'var(--accent)' : 'transparent'};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ColorSwatch = styled.span<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  flex-shrink: 0;
  background-color: ${props => props.$color};
`;

const SegmentLabel = styled.span`
  /* Absorbs the row's free space so the count and the kebab stay pinned right
     (PLAT-11182); min-width 0 is what actually lets it truncate in a flex
     row rather than pushing them off the edge. */
  flex: 1;
  min-width: 0;
  color: var(--foreground);
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ModeToggle = styled.div`
  display: inline-flex;
  align-self: flex-start;
  padding: 2px;
  border-radius: 6px;
  border: 1px solid var(--border);
  background-color: var(--surface-elevated, transparent);
`;

/* Gold active segment matching TopicFilterDropdown's mode toggle (the
   design-system selected-value treatment). */
const ModeButton = styled.button<{ $active: boolean }>`
  border: none;
  background-color: ${props =>
    props.$active ? 'var(--accent)' : 'transparent'};
  color: ${props =>
    props.$active ? 'var(--accent-foreground)' : 'var(--text-secondary)'};
  font-size: var(--text-xs);
  font-weight: ${props => (props.$active ? 600 : 500)};
  padding: 3px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    color: ${props =>
      props.$active ? 'var(--accent-foreground)' : 'var(--foreground)'};
  }
`;
